import {
  ErrorHandler,
  NgModule,
} from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  BrowserAnimationsModule,
} from '@angular/platform-browser/animations';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { ToastrModule } from 'ngx-toastr';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActionReducerMap, StoreModule } from '@ngrx/store';
import * as fromUser from './features/user/user.reducer';
import * as fromOrganization from './features/organization/organization.reducer';
import * as fromSector from './features/sector/sector.reducer';
import * as fromSite from './features/site/site.reducer';
import * as fromBatch from './features/batch/batch.reducer';
import * as fromDetection from './features/detection/detection.reducer';
import * as fromDetectionLabel from './features/detection-label/detection-label.reducer';
import * as fromCesium from './features/cesium/cesium.reducer';
import * as fromCut from './features/cut/cut.reducer';
import * as fromMeasure from './features/measure/measure.reducer';
import * as fromVolume from './features/volume/volume.reducer';
import * as fromConfig from './features/config/config.reducer';
import * as fromTheoricalLayerConfig from './features/theorical-layer/theorical-layer.reducer';
import * as fromTilesetConfig from './features/tileset-config/tileset-config.reducer';
import * as fromTracker from './features/tracker/tracker.reducer';
import { EffectsModule } from '@ngrx/effects';
import { UserEffects } from './features/user/user.effects';
import { OrganizationEffects } from './features/organization/organization.effects';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { SharedModule } from './shared/shared.module';
import { SectorEffects } from './features/sector/sector.effects';
import { SiteEffects } from './features/site/site.effects';
import { ApiService } from './services/api.service';
import { BatchEffects } from './features/batch/batch.effects';
import { DetectionEffects } from './features/detection/detection.effects';
import { VolumeEffects } from './features/volume/volume.effects';
import { WsEffects } from './features/websocket/websocket.effects';
import { environment } from '../environments/environment';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MeasureEffects } from './features/measure/measure.effects';
import { CutEffects } from './features/cut/cut.effects';
import { WorldModule } from './modules/world/world.module';
import { TranslocoRootModule } from './transloco-root.module';
import { Router } from '@angular/router';
import * as Sentry from "@sentry/angular";
import {
  TilesetConfigEffects
} from './features/tileset-config/tileset-config.effects';
import { TrackerEffects } from './features/tracker/tracker.effects';
import { PipelineEffects } from './features/pipeline/pipeline.effects';
import { GlobalErrorHandler } from './global-error-handler';
import {
  TheoricalLayerEffects
} from './features/theorical-layer/theorical-layer.effects';
import {
  RightPanelModule
} from './modules/panels/right-panel/right-panel.module';
import {
  LeftPanelModule
} from './modules/panels/left-panel/left-panel.module';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import {
  createInterceptorCondition, INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition, includeBearerTokenInterceptor
} from 'keycloak-angular';
import {
  DetectionLabelsEffects
} from './features/detection-label/detection-label.effects';

export const reducers: ActionReducerMap<any> = {
  users: fromUser.reducer,
  organizations: fromOrganization.reducer,
  sectors: fromSector.reducer,
  sites: fromSite.reducer,
  batchs: fromBatch.reducer,
  detections: fromDetection.reducer,
  detectionLabels: fromDetectionLabel.reducer,
  cesium: fromCesium.reducer,
  cuts: fromCut.reducer,
  measures: fromMeasure.reducer,
  volumes: fromVolume.reducer,
  config: fromConfig.reducer,
  theoricalLayers: fromTheoricalLayerConfig.reducer,
  tilesetConfigs: fromTilesetConfig.reducer,
  trackers: fromTracker.reducer
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const apiUrl = environment.apiUrl.replace(/\/+$/, '');
const escapedApiUrl = escapeRegExp(apiUrl);

const apiCondition =
  createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: new RegExp(
      `^${escapedApiUrl}(?:/.*)?$`,
      'i'
    ),
    bearerPrefix: 'Bearer'
  });

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            positionClass: 'toast-bottom-right',
            timeOut: 8000,
            extendedTimeOut: 2000,
            progressBar: true,
        }),
        EffectsModule.forRoot([
            UserEffects,
            OrganizationEffects,
            SectorEffects,
            SiteEffects,
            BatchEffects,
            DetectionEffects,
            DetectionLabelsEffects,
            VolumeEffects,
            MeasureEffects,
            CutEffects,
            TheoricalLayerEffects,
            TilesetConfigEffects,
            TrackerEffects,
            PipelineEffects,
            WsEffects
        ]),
        StoreModule.forRoot(reducers),
        !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 50 }) : [],
        MatToolbarModule,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatSidenavModule,
        SharedModule,
        WorldModule,
        TranslocoRootModule,
        LeftPanelModule,
        RightPanelModule], providers: [
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
        Title,
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [apiCondition]
        },
        provideHttpClient(
          withInterceptorsFromDi(),
          withInterceptors([includeBearerTokenInterceptor])
        )
    ] })
export class AppModule { }
